import { useState } from 'react';
import { api, ApiError } from '../../lib/api';

/**
 * 更正工件序列号（2026-10-30 现场需求）。
 *
 * 为什么不能用通用的「编辑」按钮：通用编辑走
 * `PATCH /api/records/physical-assets/:id`，只改工件主档一张表 ——
 * 改完序列号后，已开的质检单、合同行的登记清单仍停在旧值，
 * 「按序列号查履历/查质检」立刻对不上，现场只能把整张合同取消重做。
 *
 * 这个按钮走专用端点 `POST /api/physical-assets/:id/serial`，
 * 一次把四个投影改到位（主档 / 质检单 / 合同行登记清单 / 工单经 asset_id 自动跟随），
 * 并要求填写更正原因、留审计。
 *
 * 交互上刻意做成「二次确认 + 必填原因」：序列号是实物身份，
 * 误改会让两件工件串成一个身份，比改个备注危险得多。
 */
export function AssetSerialFixButton({
  assetId,
  currentSerial,
  onDone,
  disabled,
}: {
  assetId: string;
  currentSerial: string;
  onDone: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [serial, setSerial] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

  function start() {
    setSerial(currentSerial);
    setReason('');
    setErr('');
    setOkMsg('');
    setOpen(true);
  }

  async function submit() {
    const next = serial.trim();
    if (!next) { setErr('请填写正确的序列号'); return; }
    if (next === currentSerial) { setErr('与原序列号相同，无需更正'); return; }
    if (!reason.trim()) { setErr('请填写更正原因（序列号是实物身份，改动必须可追溯）'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.post<{ data: { updated: { inspections: number; contract_lines: number } } }>(
        `/api/physical-assets/${assetId}/serial`,
        { customer_serial: next, reason: reason.trim() },
      );
      const u = r.data?.updated || { inspections: 0, contract_lines: 0 };
      setOkMsg(`已更正为 ${next}；同步质检单 ${u.inspections} 张、合同行 ${u.contract_lines} 条`);
      setOpen(false);
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '更正失败');
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <>
        <button className="btn link" onClick={start} disabled={disabled} title="更正客户序列号（同步质检单与合同行登记）">
          更正序列号
        </button>
        {okMsg ? <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>{okMsg}</span> : null}
      </>
    );
  }

  return (
    <div className="panel-body" style={{ border: '1px solid var(--line)', borderRadius: 6, padding: 10, marginTop: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 6 }}>更正客户序列号</div>
      <p className="muted" style={{ fontSize: 11, margin: '0 0 8px' }}>
        原序列号 <b>{currentSerial}</b>。更正会同步该工件名下的质检单与合同行登记清单，并留审计。
      </p>
      <div style={{ display: 'grid', gap: 6 }}>
        <label>正确序列号
          <input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="按实物钢印 / 铭牌填写" />
        </label>
        <label>更正原因（必填）
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="例如：现场录入错误，按实物铭牌更正" />
        </label>
      </div>
      {err ? <p className="notice bad" role="alert" style={{ marginTop: 6 }}>{err}</p> : null}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button className="btn primary" onClick={() => void submit()} disabled={busy}>{busy ? '更正…' : '确认更正'}</button>
        <button className="btn" onClick={() => setOpen(false)} disabled={busy}>取消</button>
      </div>
    </div>
  );
}
